import React from "react";

import { Menu } from "@material-ui/icons";
import { Button, FormControl, FormLabel, Typography } from "@portex-pro/ui-components";
import { RoutingType, Mode, Address } from "api/types/generated-types";
import LocationPicker from "components/addresses/LocationPicker";
import { Guide } from "components/Guide";
import SingleCalendarPicker from "components/SingleCalendarPicker";
import { DateTime } from "luxon";
import FormSelectView from "pages/shipper/pages/request-quote/components/FormSelectView";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IncotermsEnum } from "types/Incoterms";
import { RoutingTypeEnum } from "types/RoutingType";

import MemoizedTextInput from "../../_components/MemoizedTextInput";
import RoutingTabPanel from "../../_components/RoutingTabPanel";
import StepCollapse from "../../_components/StepCollapse";
import { useCreateStepCollapseProps } from "../../_hooks/useStepCollapse";
import { useQuoteRequestSelector } from "../../_store";
import { quoteRequestActions } from "../../_store/quoteRequestSliceV2";
import { QuoteStepIndex } from "../../types";
import { FinalSchema } from "../_schemas";

interface RoutingStepTagProps {
  originStop?: {
    address?: {
      name?: string | null;
      city?: string | null;
      country_name?: string | null;
      port_name?: string | null;
    } | null;
  };
  originPort?: {
    address?: {
      name?: string | null;
      city?: string | null;
      country_name?: string | null;
      port_name?: string | null;
    } | null;
  };
  destinationPort?: {
    address?: {
      name?: string | null;
      city?: string | null;
      country_name?: string | null;
      port_name?: string | null;
    } | null;
  };
  destinationStop?: {
    address?: {
      name?: string | null;
      city?: string | null;
      country_name?: string | null;
      port_name?: string | null;
    } | null;
  };
  cargoReadyDate?: string;
  routingType?: RoutingType;
}

const RoutingStepTag: React.FC<RoutingStepTagProps> = ({
  originStop,
  originPort,
  destinationPort,
  destinationStop,
  cargoReadyDate,
  routingType,
}) => {
  if (!routingType) {
    return <></>;
  }

  let originName = "";
  let destinationName = "";

  if (routingType === RoutingType.P2P) {
    if (!originPort?.address || !destinationPort?.address) {
      return <></>;
    }
    originName = originPort.address.port_name || "";
    destinationName = destinationPort.address.port_name || "";
  } else if (routingType === RoutingType.P2D) {
    if (!originPort?.address || !destinationStop?.address) {
      return <></>;
    }
    originName = originPort.address.port_name || "";
    destinationName = destinationStop.address.country_name || destinationStop.address.city || "";
  } else if (routingType === RoutingType.D2P) {
    if (!originStop?.address || !destinationPort?.address) {
      return <></>;
    }
    originName = originStop.address.country_name || originStop.address.city || "";
    destinationName = destinationPort.address.port_name || "";
  } else if (routingType === RoutingType.D2D) {
    if (!originStop?.address || !destinationStop?.address) {
      return <></>;
    }
    originName = originStop.address.country_name || originStop.address.city || "";
    destinationName = destinationStop.address.country_name || destinationStop.address.city || "";
  }

  if (!originName || !destinationName) {
    return <></>;
  }

  let displayDate = "";
  if (cargoReadyDate) {
    const date = DateTime.fromISO(cargoReadyDate);
    if (date.isValid) {
      displayDate = date.toFormat("MMM dd");
    }
  }

  return (
    <div className="inline-flex items-center bg-blue-50 rounded-md px-2 py-1">
      <span className="text-blue-600 font-bold text-sm">
        {originName} – {destinationName}
      </span>
      {displayDate && <span className="text-blue-600 text-sm ml-1">{displayDate}</span>}
    </div>
  );
};

export const RoutingStep: React.FC<{ onSubmit: (data: FinalSchema) => void }> = ({ onSubmit }) => {
  const { t } = useTranslation(["quoteRequest", "common"]);
  const [showPrefNotes, setShowPrefNotes] = React.useState(false);
  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();
  const setQuoteRequestActionByPath = quoteRequestActions.useSetQuoteRequestByPath();
  const {
    quoteRequest: { stops },
    stepIndex,
  } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const setStepIndex = quoteRequestActions.useSetStepIndex();

  const methods = useFormContext<FinalSchema>();

  const watchedValues = methods.watch();
  const selectedRouting = watchedValues.routing_type as unknown as RoutingType;
  const cargoReadyDate = watchedValues.cargo_ready_date;
  const originStop = watchedValues.originStop;
  const originPort = watchedValues.originPort;
  const destinationPort = watchedValues.destinationPort;
  const destinationStop = watchedValues.destinationStop;

  const indexOffset = selectedRouting === RoutingType.D2D || selectedRouting === RoutingType.D2P ? 0 : 1;

  const submit = (data: FinalSchema) => {
    onSubmit(data);
    setStepIndex(QuoteStepIndex.Service);
    setIsOpen(false);
  };

  const showPickup = selectedRouting === RoutingType.D2D || selectedRouting === RoutingType.D2P;
  const showDelivery = selectedRouting === RoutingType.D2D || selectedRouting === RoutingType.P2D;

  const { baseStepCollapseProps, setIsOpen } = useCreateStepCollapseProps({
    expectedStepIndex: QuoteStepIndex.Routing,
    name: t("common:routing"),
    number: 1,
  });

  return (
    <StepCollapse
      {...baseStepCollapseProps}
      tag={
        <RoutingStepTag
          originStop={originStop}
          originPort={originPort}
          destinationPort={destinationPort}
          destinationStop={destinationStop}
          cargoReadyDate={cargoReadyDate}
          routingType={selectedRouting}
        />
      }
      footer={
        stepIndex <= QuoteStepIndex.Routing ? (
          <div className="flex justify-end mt-2 w-full">
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={methods.handleSubmit(submit)}
              style={{ width: 150 }}
            >
              {t("quoteRequest:RoutingStep.nextButton")}
            </Button>
          </div>
        ) : null
      }
    >
      <div className="space-y-8 my-6">
        {/* Incoterms Section */}
        <div className="grid grid-cols-4 gap-4">
          <Controller
            name="incoterms"
            control={methods.control}
            render={({ field, fieldState }) => (
              <Guide className="flex flex-col w-full" showGuide={!field.value && !fieldState.error}>
                <FormSelectView
                  value={field.value}
                  items={Object.values(IncotermsEnum)}
                  getItemCopy={(incoterm) => t(`common:incoterms_map.${incoterm}`)}
                  onChange={(value) => setQuoteRequestAction({ load_spec: { incoterms: value } })}
                  formControlProps={{ required: true }}
                  highlight={!!methods.formState.errors.incoterms}
                  label={t("common:incoterms")}
                />
                {methods.formState.errors.incoterms && (
                  <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                    {methods.formState.errors.incoterms.message}
                  </Typography>
                )}
              </Guide>
            )}
          />

          {/* Select Routing Section */}
          <div className="col-span-2">
            <FormControl fullWidth>
              <FormLabel required>{t("quoteRequest:RoutingStep.selectRouting")}</FormLabel>
              <Controller
                name="routing_type"
                control={methods.control}
                render={({ field, fieldState }) => (
                  <Guide showGuide={!field.value && !fieldState.error}>
                    <RoutingTabPanel
                      selected={field.value as unknown as RoutingType}
                      className="guideGroup"
                      onSelect={(routing) => {
                        const dropOrigin =
                          (field.value === "D2P" || field.value === "D2D" || field.value === undefined) &&
                          (routing === "P2D" || routing === "P2P");
                        const addOrigin =
                          (routing === "D2P" || routing === "D2D") && (field.value === "P2D" || field.value === "P2P");
                        const dropDelivery =
                          (field.value === "P2D" || field.value === "D2D" || field.value === undefined) &&
                          (routing === "D2P" || routing === "P2P");
                        const addDelivery =
                          (routing === "P2D" || routing === "D2D") && (field.value === "D2P" || field.value === "P2P");

                        const newStops = stops?.slice(dropOrigin ? 1 : 0, dropDelivery ? -1 : undefined);

                        if (addOrigin) {
                          newStops?.unshift({});
                        }

                        if (addDelivery) {
                          newStops?.push({});
                        }

                        setQuoteRequestAction({
                          load_spec: { routing_type: routing as unknown as RoutingTypeEnum },
                          stops: newStops,
                        });
                      }}
                      hasError={!!methods.formState.errors.routing_type}
                    />
                  </Guide>
                )}
              />
              {methods.formState.errors.routing_type && (
                <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                  {methods.formState.errors.routing_type.message}
                </Typography>
              )}
            </FormControl>
          </div>
        </div>

        {/* Location Fields Grid */}
        {selectedRouting && (
          <div className="grid grid-cols-4 gap-4">
            {/* Pickup Location */}
            {showPickup && (
              <div>
                <FormControl margin="dense" fullWidth>
                  <FormLabel required>{t("quoteRequest:RoutingStep.pickup")}</FormLabel>
                  <Controller
                    name="originStop"
                    control={methods.control}
                    render={({ field, fieldState }) => (
                      <Guide showGuide={!field.value?.address && !fieldState.error}>
                        <LocationPicker
                          mode={Mode.Fcl}
                          value={field.value?.address as unknown as Address}
                          onChange={(address) =>
                            setQuoteRequestActionByPath({ path: `stops.${0}.address`, value: address })
                          }
                          startIconPallete="grey"
                          toDoor={true}
                          placeholder={t("quoteRequest:RoutingStep.selectLocation")}
                          highlight={!!methods.formState.errors.originStop}
                        />
                      </Guide>
                    )}
                  />
                  {methods.formState.errors.originStop && (
                    <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                      {methods.formState.errors.originStop.message}
                    </Typography>
                  )}
                </FormControl>
              </div>
            )}

            {/* Port of Loading */}
            <div>
              <FormControl margin="dense" fullWidth>
                <FormLabel required={!showPickup}>{t("quoteRequest:RoutingStep.portOfLoading")}</FormLabel>
                <Controller
                  name="originPort"
                  control={methods.control}
                  render={({ field, fieldState }) => (
                    <Guide showGuide={!field.value?.address && !fieldState.error && !showPickup}>
                      <LocationPicker
                        mode={Mode.Fcl}
                        value={field.value?.address as unknown as Address}
                        onChange={(address) =>
                          setQuoteRequestActionByPath({ path: `stops.${1 - indexOffset}.address`, value: address })
                        }
                        startIconPallete="grey"
                        toDoor={false}
                        placeholder={t("quoteRequest:RoutingStep.selectPort")}
                        highlight={!!methods.formState.errors.originPort}
                      />
                    </Guide>
                  )}
                />
                {methods.formState.errors.originPort && (
                  <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                    {methods.formState.errors.originPort.message}
                  </Typography>
                )}
              </FormControl>
            </div>

            {/* Port of Discharge */}
            <div>
              <FormControl margin="dense" fullWidth>
                <FormLabel required={!showDelivery}>{t("quoteRequest:RoutingStep.portOfDischarge")}</FormLabel>
                <Controller
                  name="destinationPort"
                  control={methods.control}
                  render={({ field, fieldState }) => (
                    <Guide showGuide={!field.value?.address && !fieldState.error && !showDelivery}>
                      <LocationPicker
                        mode={Mode.Fcl}
                        value={field.value?.address as unknown as Address}
                        onChange={(address) =>
                          setQuoteRequestActionByPath({ path: `stops.${2 - indexOffset}.address`, value: address })
                        }
                        startIconPallete="grey"
                        toDoor={false}
                        placeholder={t("quoteRequest:RoutingStep.selectPort")}
                        highlight={!!methods.formState.errors.destinationPort}
                      />
                    </Guide>
                  )}
                />
                {methods.formState.errors.destinationPort && (
                  <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                    {methods.formState.errors.destinationPort.message}
                  </Typography>
                )}
              </FormControl>
            </div>

            {/* Delivery Location */}
            {showDelivery && (
              <div>
                <FormControl margin="dense" fullWidth>
                  <FormLabel required>{t("quoteRequest:RoutingStep.delivery")}</FormLabel>
                  <Controller
                    name="destinationStop"
                    control={methods.control}
                    render={({ field, fieldState }) => (
                      <Guide showGuide={!field.value?.address && !fieldState.error}>
                        <LocationPicker
                          mode={Mode.Fcl}
                          value={field.value?.address as unknown as Address}
                          onChange={(address) =>
                            setQuoteRequestActionByPath({ path: `stops.${3 - indexOffset}.address`, value: address })
                          }
                          startIconPallete="grey"
                          toDoor={true}
                          placeholder={t("quoteRequest:RoutingStep.selectLocation")}
                          highlight={!!methods.formState.errors.destinationStop}
                        />
                      </Guide>
                    )}
                  />
                  {methods.formState.errors.destinationStop && (
                    <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                      {methods.formState.errors.destinationStop.message}
                    </Typography>
                  )}
                </FormControl>
              </div>
            )}
          </div>
        )}

        {/* Date Fields */}
        {selectedRouting && (
          <div className="grid grid-cols-2 gap-6">
            <div>
              <FormControl margin="dense" fullWidth>
                <FormLabel required>{t("quoteRequest:RoutingStep.cargoReadyDate")}</FormLabel>
                <Controller
                  name="cargo_ready_date"
                  control={methods.control}
                  render={({ field, fieldState }) => (
                    <Guide showGuide={!field.value && !fieldState.error}>
                      <SingleCalendarPicker
                        value={field.value ? DateTime.fromISO(field.value) : null}
                        format="DD"
                        onChange={(value) => setQuoteRequestAction({ load_spec: { cargo_ready_date: value?.toISO() } })}
                        startIconPallete="grey"
                        highlight={!!methods.formState.errors.cargo_ready_date}
                      />
                    </Guide>
                  )}
                />
                {methods.formState.errors.cargo_ready_date && (
                  <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                    {methods.formState.errors.cargo_ready_date.message}
                  </Typography>
                )}
              </FormControl>
            </div>

            <div>
              <FormControl margin="dense" fullWidth>
                <FormLabel>{t("quoteRequest:RoutingStep.targetDeliveryDate")}</FormLabel>
                <Controller
                  name="target_delivery_date"
                  control={methods.control}
                  render={({ field }) => (
                    <SingleCalendarPicker
                      minDate={cargoReadyDate ? DateTime.fromISO(cargoReadyDate) : undefined}
                      value={field.value ? DateTime.fromISO(field.value) : null}
                      format="DD"
                      placeholder={t("quoteRequest:RoutingStep.selectDateOptional")}
                      onChange={(value) =>
                        setQuoteRequestAction({ load_spec: { target_delivery_date: value?.toISO() } })
                      }
                      clearable
                      startIconPallete="grey"
                      highlight={!!methods.formState.errors.target_delivery_date}
                    />
                  )}
                />
                {methods.formState.errors.target_delivery_date && (
                  <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                    {methods.formState.errors.target_delivery_date.message}
                  </Typography>
                )}
              </FormControl>
            </div>
          </div>
        )}

        {selectedRouting && (
          <div>
            <button
              type="button"
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium"
              onClick={() => setShowPrefNotes((prev) => !prev)}
            >
              <Menu />
              <span>{t("quoteRequest:RoutingStep.addCarrierPreferences")}</span>
            </button>
          </div>
        )}
        {showPrefNotes && (
          <Controller
            name="carrierRoutingPrefNotes"
            control={methods.control}
            render={({ field }) => (
              <MemoizedTextInput
                {...field}
                onChange={(e) => setQuoteRequestAction({ carrier_routing_pref_notes: e.target.value })}
                placeholder={t("quoteRequest:RoutingStep.carrierRoutingPrefPlaceholder")}
                multiline
                rows={3}
                fullWidth
                style={{ marginTop: 8, marginBottom: 12 }}
              />
            )}
          />
        )}
      </div>
    </StepCollapse>
  );
};
